import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { connect } from 'react-redux';

// own functions
import * as ownFunctions from './ContractStages.Selector.Functions';
import { ContractStage } from '../../../../models/Entities/Contracts/ContractStage';
import { 
  Field, 
  Label, 
  Tag, 
  TagPicker, 
  TagPickerControl, 
  TagPickerGroup, 
  TagPickerInput, 
  TagPickerList, 
  TagPickerOption, 
  TagPickerProps,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';

const ContractStagesSelectorComponent = (props: ownFunctions.Props) => {
  // Estado local para las opciones seleccionadas
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  
  // Ref para prevenir actualizaciones circulares
  const isInternalUpdate = useRef(false);
  const previousStagesRef = useRef<ContractStage[]>([]);

  // Cargar las etapas al montar el componente
  useEffect(() => {
    if (props.ContractId) {
      props.GetAllContractsStage(props.ContractId);
    }
  }, [props.ContractId, props.GetAllContractsStage]);

  // Memoizar las opciones disponibles del TagPicker
  const tagPickerOptions = useMemo(() => {
    if (!props.Stages || props.Stages.length === 0) return [];
    
    return props.Stages
      .map((stage) => stage.stageName)
      .filter((stageName: string) => !selectedOptions.includes(stageName));
  }, [props.Stages, selectedOptions]);

  // Función para comparar arrays de IDs
  const areStageIdsEqual = useCallback((ids1: number[] | undefined, ids2: number[]): boolean => {
    if (!ids1 || ids1.length !== ids2.length) return false;
    const sorted1 = [...ids1].sort((a, b) => a - b);
    const sorted2 = [...ids2].sort((a, b) => a - b);
    return sorted1.every((id, index) => id === sorted2[index]);
  }, []);

  // Sincronizar selectedOptions cuando props.ContractStagesSelected cambia (desde el padre)
  useEffect(() => {
    // Evitar actualización si el cambio vino de este componente
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    if (!props.Stages || props.Stages.length === 0) {
      return;
    }

    if (props.ContractStagesSelected && props.ContractStagesSelected.length > 0) {
      const selectedStageNames = props.ContractStagesSelected
        .map((stageId: number) => {
          const stage = props.Stages?.find((s: ContractStage) => s.id === stageId);
          return stage?.stageName;
        })
        .filter((name): name is string => name !== undefined);
      
      // Solo actualizar si realmente cambió
      if (JSON.stringify(selectedStageNames.sort()) !== JSON.stringify(selectedOptions.sort())) {
        setSelectedOptions(selectedStageNames);
      }
    } else {
      // Limpiar selección si no hay selección en props
      if (selectedOptions.length > 0) {
        setSelectedOptions([]);
      }
    }
  }, [props.ContractStagesSelected, props.Stages]);

  // Callback para manejar la selección de opciones
  const onOptionSelect: TagPickerProps["onOptionSelect"] = useCallback((e, data) => {
    if (data.value === "no-options") {
      setSelectedOptions([]);
      isInternalUpdate.current = true;
      props.onSelectedContractStage([]);
      return;
    }

    let newSelectedOptions: string[];
    
    // Detectar si es una operación de remoción (dismiss) o adición
    const isRemoving = data.selectedOptions.length < selectedOptions.length;
    
    if (isRemoving) {
      // Si se está removiendo, usar directamente selectedOptions del evento
      newSelectedOptions = data.selectedOptions;
    } else {
      // Si se está agregando
      if (!props.MultiselectEnable) {
        // Modo selección única: reemplazar con el nuevo valor
        newSelectedOptions = [data.value];
      } else {
        // Modo multi-selección
        newSelectedOptions = data.selectedOptions;
      }
    }
    
    // Si MultiselectEnable es false, actúa como límite de 1
    const effectiveLimit = props.MultiselectEnable ? undefined : 1;
    
    // Verificar límite de selección si está en modo multi-select
    if (effectiveLimit && newSelectedOptions.length > effectiveLimit && !isRemoving) {
      return; // No permitir más de lo especificado
    }
    
    setSelectedOptions(newSelectedOptions);

    // Obtener los objetos ContractStage correspondientes
    const selectedStages = props.Stages?.filter((stage: ContractStage) => 
      newSelectedOptions.includes(stage.stageName)
    ) || [];

    // Comparar con la selección anterior para evitar actualizaciones innecesarias
    const newStageIds = selectedStages.map(s => s.id);
    const prevStageIds = previousStagesRef.current.map(s => s.id);
    
    if (!areStageIdsEqual(newStageIds, prevStageIds)) {
      previousStagesRef.current = selectedStages;
      isInternalUpdate.current = true;
      props.onSelectedContractStage(selectedStages);
    }
  }, [props.MultiselectEnable, props.Stages, props.onSelectedContractStage, areStageIdsEqual, selectedOptions]);

  // Renderizar spinner de carga
  if (props.isLoading) {
    return (
      <Field 
        label={props.showTitle !== false ? "Seleccione una visita" : undefined} 
        style={{ maxWidth: 400 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0' }}>
          <Spinner size="tiny" label="Cargando etapas del contrato..." />
        </div>
      </Field>
    );
  }

  // Renderizar error si falló la carga
  if (props.failOnLoading && props.error) {
    return (
      <Field 
        label={props.showTitle !== false ? "Seleccione una visita" : undefined} 
        style={{ maxWidth: 400 }}
      >
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Error al cargar etapas</MessageBarTitle>
            {props.error.ErrorMessage || 'No se pudieron cargar las etapas del contrato. Por favor, intente nuevamente.'}
          </MessageBarBody>
        </MessageBar>
      </Field>
    );
  }

  // Verificar si no hay etapas disponibles
  const hasNoStages = !props.Stages || props.Stages.length === 0;
  const hasContractId = props.ContractId !== undefined && props.ContractId > 0;
  const isContractWithoutStages = hasContractId && hasNoStages && !props.isLoading;
  const noContractSelected = props.ContractId === undefined || props.ContractId === 0;

  return (
    <Field 
      label={props.showTitle !== false ? "Seleccione una visita" : undefined} 
      style={{ maxWidth: 400 }}
      hint={props.noteText || (!props.MultiselectEnable ? "Puede seleccionar solo 1 visita" : undefined)}
    >
      {noContractSelected && (
        <MessageBar intent="info" style={{ marginBottom: '8px' }}>
          <MessageBarBody>
            Primero debe seleccionar un contrato para ver las visitas disponibles.
          </MessageBarBody>
        </MessageBar>
      )}
      {isContractWithoutStages && !noContractSelected && (
        <MessageBar intent="warning" style={{ marginBottom: '8px' }}>
          <MessageBarBody>
            Este contrato no tiene visitas definidas. Por favor, configure las visitas del contrato.
          </MessageBarBody>
        </MessageBar>
      )}
      <TagPicker
        onOptionSelect={onOptionSelect}
        selectedOptions={selectedOptions}
        disabled={hasNoStages}
      >
        <TagPickerControl>
          <TagPickerGroup aria-label="visitas seleccionadas">
            {selectedOptions.map((option) => (
              <Tag
                key={option}
                shape="rounded"
                value={option}
                dismissible
                dismissIcon={<DismissRegular />}
              >
                {option}
              </Tag>
            ))}
          </TagPickerGroup>
          <TagPickerInput 
            aria-label="Seleccione una visita"
            placeholder={
              hasNoStages 
                ? "No hay etapas disponibles" 
                : selectedOptions.length === 0 
                  ? "Seleccione..." 
                  : ""
            }
          />
        </TagPickerControl>
        <TagPickerList>
          {tagPickerOptions.length > 0 ? (
            tagPickerOptions.map((option) => (
              <TagPickerOption
                value={option}
                key={option}
              >
                {option}
              </TagPickerOption>
            ))
          ) : (
            <TagPickerOption value="no-options">
              {hasNoStages ? "No hay etapas disponibles" : "No hay más opciones"}
            </TagPickerOption>
          )}
        </TagPickerList>
      </TagPicker>
    </Field>
  );
};

export default connect(
  ownFunctions.mapStateToProps,
  ownFunctions.mapDispatchToProps
)(ContractStagesSelectorComponent as any);
